<script setup>
import { ref, computed } from 'vue';
import Modal from 'dashboard/components/Modal.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import BrasilApiAPI from 'dashboard/api/brasilApi';

const show = defineModel('show', { type: Boolean, default: false });

const emit = defineEmits(['close']);

const searchQuery = ref('');
const loading = ref(false);
const contacts = ref([]);
const external = ref(null);
const error = ref('');
const activeTab = ref('contacts');

const formattedDocument = computed(() => {
  const digits = searchQuery.value.replace(/\D/g, '');
  if (digits.length <= 11) {
    return digits
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
  }
  return digits
    .replace(/(\d{2})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1/$2')
    .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
});

const documentType = computed(() => {
  const digits = searchQuery.value.replace(/\D/g, '');
  if (digits.length <= 11) return 'CPF';
  return 'CNPJ';
});

const isCnpj = computed(() => documentType.value === 'CNPJ');

const hasContacts = computed(() => contacts.value.length > 0);
const hasExternal = computed(() => external.value && !external.value.error);

const onInput = event => {
  searchQuery.value = event.target.value.replace(/\D/g, '').slice(0, 14);
};

const consultar = async () => {
  const digits = searchQuery.value.replace(/\D/g, '');
  if (digits.length < 3) {
    error.value = 'Digite pelo menos 3 digitos para buscar.';
    return;
  }

  loading.value = true;
  error.value = '';
  contacts.value = [];
  external.value = null;

  try {
    const response = await BrasilApiAPI.consultar(digits);
    contacts.value = response.data.contacts || [];
    external.value = response.data.external;
    activeTab.value = contacts.value.length > 0 ? 'contacts' : 'external';
  } catch (err) {
    error.value =
      err.response?.data?.error || 'Erro ao consultar. Tente novamente.';
  } finally {
    loading.value = false;
  }
};

const limpar = () => {
  searchQuery.value = '';
  contacts.value = [];
  external.value = null;
  error.value = '';
  activeTab.value = 'contacts';
};

const fechar = () => {
  limpar();
  emit('close');
};

const formatDate = dateStr => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('pt-BR');
};
</script>

<template>
  <Modal v-model:show="show" size="medium" @close="fechar">
    <div class="flex flex-col p-6">
      <!-- Header -->
      <div class="mb-4 flex items-center gap-3">
        <div
          class="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-100 dark:bg-emerald-900"
        >
          <i
            class="i-lucide-search text-lg text-emerald-600 dark:text-emerald-400"
          />
        </div>
        <div>
          <h3 class="text-base font-semibold text-ash-900 dark:text-ash-100">
            Consulta CPF / CNPJ
          </h3>
          <p class="text-xs text-ash-500 dark:text-ash-400">
            Busca em contatos internos + dados publicos (BrasilAPI)
          </p>
        </div>
      </div>

      <!-- Search Input -->
      <div class="mb-4 flex gap-2">
        <input
          :value="formattedDocument"
          class="flex-1 rounded-lg border border-ash-200 bg-white px-3 py-2 text-sm text-ash-900 placeholder:text-ash-400 focus:border-emerald-500 focus:outline-none dark:border-ash-700 dark:bg-ash-900 dark:text-ash-100"
          placeholder="Digite CPF, CNPJ, nome, email ou telefone..."
          @input="onInput"
          @keydown.enter="consultar"
        />
        <NextButton
          label="Consultar"
          icon="i-lucide-search"
          color="blue"
          :disabled="loading || searchQuery.replace(/\D/g, '').length < 3"
          @click="consultar"
        />
      </div>

      <!-- Loading -->
      <div v-if="loading" class="flex items-center justify-center py-8">
        <i class="i-lucide-loader-2 animate-spin text-2xl text-emerald-500" />
        <span class="ml-2 text-sm text-ash-500">Consultando...</span>
      </div>

      <!-- Error -->
      <div
        v-if="error"
        class="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300"
      >
        <i class="i-lucide-alert-circle mr-1 inline-block align-middle" />
        {{ error }}
      </div>

      <!-- Results -->
      <div v-if="(hasContacts || hasExternal) && !loading">
        <!-- Tabs -->
        <div class="mb-3 flex gap-1 rounded-lg bg-ash-100 p-1 dark:bg-ash-800">
          <button
            class="flex-1 rounded-md px-3 py-1.5 text-xs font-medium transition-colors"
            :class="
              activeTab === 'contacts'
                ? 'bg-white text-ash-900 shadow-sm dark:bg-ash-700 dark:text-ash-100'
                : 'text-ash-500 hover:text-ash-700 dark:text-ash-400'
            "
            @click="activeTab = 'contacts'"
          >
            <i class="i-lucide-users mr-1 inline-block align-middle text-sm" />
            Contatos ({{ contacts.length }})
          </button>
          <button
            class="flex-1 rounded-md px-3 py-1.5 text-xs font-medium transition-colors"
            :class="
              activeTab === 'external'
                ? 'bg-white text-ash-900 shadow-sm dark:bg-ash-700 dark:text-ash-100'
                : 'text-ash-500 hover:text-ash-700 dark:text-ash-400'
            "
            @click="activeTab = 'external'"
          >
            <i class="i-lucide-globe mr-1 inline-block align-middle text-sm" />
            BrasilAPI
          </button>
        </div>

        <!-- Contacts Tab -->
        <div
          v-if="activeTab === 'contacts'"
          class="max-h-80 space-y-2 overflow-y-auto"
        >
          <div
            v-if="!hasContacts"
            class="py-6 text-center text-sm text-ash-400"
          >
            Nenhum contato encontrado na base interna.
          </div>
          <div
            v-for="contact in contacts"
            :key="contact.id"
            class="rounded-lg border border-ash-200 p-3 dark:border-ash-700"
          >
            <div class="mb-2 flex items-center justify-between">
              <span
                class="text-sm font-semibold text-ash-900 dark:text-ash-100"
              >
                {{ contact.name || 'Sem nome' }}
              </span>
              <span
                class="rounded-full bg-ash-100 px-2 py-0.5 text-xs text-ash-500 dark:bg-ash-800"
              >
                {{ contact.conversations_count }} conversas
              </span>
            </div>
            <div class="space-y-1">
              <div v-if="contact.phone_number" class="flex items-center gap-2">
                <i
                  class="i-lucide-phone text-xs text-ash-400 dark:text-ash-500"
                />
                <span class="text-xs text-ash-700 dark:text-ash-300">{{
                  contact.phone_number
                }}</span>
              </div>
              <div v-if="contact.email" class="flex items-center gap-2">
                <i
                  class="i-lucide-mail text-xs text-ash-400 dark:text-ash-500"
                />
                <span class="text-xs text-ash-700 dark:text-ash-300">{{
                  contact.email
                }}</span>
              </div>
              <div
                v-if="contact.city || contact.location"
                class="flex items-center gap-2"
              >
                <i
                  class="i-lucide-map-pin text-xs text-ash-400 dark:text-ash-500"
                />
                <span class="text-xs text-ash-700 dark:text-ash-300">{{
                  contact.city || contact.location
                }}</span>
              </div>
              <div
                v-if="contact.company"
                class="flex items-center gap-2"
              >
                <i
                  class="i-lucide-building-2 text-xs text-ash-400 dark:text-ash-500"
                />
                <span class="text-xs text-ash-700 dark:text-ash-300">{{
                  contact.company
                }}</span>
              </div>
              <div v-if="contact.last_activity_at" class="flex items-center gap-2">
                <i
                  class="i-lucide-clock text-xs text-ash-400 dark:text-ash-500"
                />
                <span class="text-xs text-ash-400">
                  Ultima atividade: {{ formatDate(contact.last_activity_at) }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- External Tab -->
        <div v-if="activeTab === 'external'">
          <div
            v-if="!hasExternal"
            class="py-6 text-center text-sm text-ash-400"
          >
            <template v-if="external && external.error">
              {{ external.error }}
            </template>
            <template v-else>
              Digite pelo menos 11 digitos (CPF) ou 14 (CNPJ) para consulta
              externa.
            </template>
          </div>
          <div
            v-if="hasExternal"
            class="rounded-lg border border-ash-200 dark:border-ash-700"
          >
            <!-- Header Badge -->
            <div
              class="flex items-center justify-between border-b border-ash-200 px-4 py-2 dark:border-ash-700"
            >
              <span
                class="rounded-full px-2.5 py-0.5 text-xs font-semibold"
                :class="
                  isCnpj
                    ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                    : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300'
                "
              >
                {{ documentType }}
              </span>
            </div>

            <!-- Data Rows -->
            <div class="divide-y divide-ash-100 dark:divide-ash-800">
              <!-- CPF Fields -->
              <template v-if="!isCnpj">
                <div class="flex px-4 py-2.5">
                  <span
                    class="w-32 shrink-0 text-xs font-medium text-ash-500 dark:text-ash-400"
                    >CPF</span
                  >
                  <span class="text-sm text-ash-900 dark:text-ash-100">{{
                    external.cpf
                  }}</span>
                </div>
                <div class="flex px-4 py-2.5">
                  <span
                    class="w-32 shrink-0 text-xs font-medium text-ash-500 dark:text-ash-400"
                    >Valido</span
                  >
                  <span
                    class="text-sm font-medium"
                    :class="
                      external.valido === 'Sim'
                        ? 'text-emerald-600 dark:text-emerald-400'
                        : 'text-red-600 dark:text-red-400'
                    "
                  >
                    {{ external.valido }}
                  </span>
                </div>
                <div v-if="external.estados" class="flex px-4 py-2.5">
                  <span
                    class="w-32 shrink-0 text-xs font-medium text-ash-500 dark:text-ash-400"
                    >Estados</span
                  >
                  <span class="text-sm text-ash-900 dark:text-ash-100">{{
                    external.estados
                  }}</span>
                </div>
              </template>

              <!-- CNPJ Fields -->
              <template v-if="isCnpj">
                <div class="flex px-4 py-2.5">
                  <span
                    class="w-32 shrink-0 text-xs font-medium text-ash-500 dark:text-ash-400"
                    >CNPJ</span
                  >
                  <span class="text-sm text-ash-900 dark:text-ash-100">{{
                    external.cnpj
                  }}</span>
                </div>
                <div class="flex px-4 py-2.5">
                  <span
                    class="w-32 shrink-0 text-xs font-medium text-ash-500 dark:text-ash-400"
                    >Razao Social</span
                  >
                  <span class="text-sm text-ash-900 dark:text-ash-100">{{
                    external.razao_social
                  }}</span>
                </div>
                <div v-if="external.nome_fantasia" class="flex px-4 py-2.5">
                  <span
                    class="w-32 shrink-0 text-xs font-medium text-ash-500 dark:text-ash-400"
                    >Nome Fantasia</span
                  >
                  <span class="text-sm text-ash-900 dark:text-ash-100">{{
                    external.nome_fantasia
                  }}</span>
                </div>
                <div class="flex px-4 py-2.5">
                  <span
                    class="w-32 shrink-0 text-xs font-medium text-ash-500 dark:text-ash-400"
                    >Situacao</span
                  >
                  <span class="text-sm text-ash-900 dark:text-ash-100">{{
                    external.situacao_cadastral
                  }}</span>
                </div>
                <div v-if="external.cnae_principal" class="flex px-4 py-2.5">
                  <span
                    class="w-32 shrink-0 text-xs font-medium text-ash-500 dark:text-ash-400"
                    >CNAE</span
                  >
                  <span class="text-sm text-ash-900 dark:text-ash-100">{{
                    external.cnae_principal
                  }}</span>
                </div>
                <div v-if="external.porte" class="flex px-4 py-2.5">
                  <span
                    class="w-32 shrink-0 text-xs font-medium text-ash-500 dark:text-ash-400"
                    >Porte</span
                  >
                  <span class="text-sm text-ash-900 dark:text-ash-100">{{
                    external.porte
                  }}</span>
                </div>
                <div v-if="external.capital_social" class="flex px-4 py-2.5">
                  <span
                    class="w-32 shrink-0 text-xs font-medium text-ash-500 dark:text-ash-400"
                    >Capital Social</span
                  >
                  <span class="text-sm text-ash-900 dark:text-ash-100">
                    R$
                    {{
                      Number(external.capital_social).toLocaleString('pt-BR', {
                        minimumFractionDigits: 2,
                      })
                    }}
                  </span>
                </div>
                <div v-if="external.municipio" class="flex px-4 py-2.5">
                  <span
                    class="w-32 shrink-0 text-xs font-medium text-ash-500 dark:text-ash-400"
                    >Endereco</span
                  >
                  <span class="text-sm text-ash-900 dark:text-ash-100">
                    {{
                      [
                        external.logradouro,
                        external.numero,
                        external.bairro,
                        external.municipio,
                        external.uf,
                      ]
                        .filter(Boolean)
                        .join(', ')
                    }}
                  </span>
                </div>
                <div v-if="external.cep" class="flex px-4 py-2.5">
                  <span
                    class="w-32 shrink-0 text-xs font-medium text-ash-500 dark:text-ash-400"
                    >CEP</span
                  >
                  <span class="text-sm text-ash-900 dark:text-ash-100">{{
                    external.cep
                  }}</span>
                </div>
                <div v-if="external.telefone" class="flex px-4 py-2.5">
                  <span
                    class="w-32 shrink-0 text-xs font-medium text-ash-500 dark:text-ash-400"
                    >Telefone</span
                  >
                  <span class="text-sm text-ash-900 dark:text-ash-100">{{
                    external.telefone
                  }}</span>
                </div>
                <div v-if="external.email" class="flex px-4 py-2.5">
                  <span
                    class="w-32 shrink-0 text-xs font-medium text-ash-500 dark:text-ash-400"
                    >Email</span
                  >
                  <span class="text-sm text-ash-900 dark:text-ash-100">{{
                    external.email
                  }}</span>
                </div>
              </template>
            </div>
          </div>
        </div>
      </div>

      <!-- No results -->
      <div
        v-if="
          !loading && !error && !hasContacts && !hasExternal && searchQuery
        "
        class="py-6 text-center text-sm text-ash-400"
      >
        Nenhum resultado encontrado.
      </div>
    </div>
  </Modal>
</template>
